import React, { useState, useEffect } from 'react';
import { User, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { getClientsByLaundry } from '@/pages/parametrizacion/clients/Services/clients.services';

const SeleccionarClienteModal = ({ isOpen, onClose, lavanderiaId, onClienteSeleccionado }) => {
  const [clientes, setClientes] = useState([]);
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && lavanderiaId) {
      loadClientes();
    }
  }, [isOpen, lavanderiaId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = clientes.filter(cliente =>
        cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cliente.nit?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredClientes(filtered);
    } else {
      setFilteredClientes(clientes);
    }
  }, [searchTerm, clientes]);

  const loadClientes = async () => {
    setIsLoading(true);
    try {
      const response = await getClientsByLaundry(lavanderiaId);
      const clientesData = response.data || [];
      setClientes(clientesData);
      setFilteredClientes(clientesData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los clientes',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCliente = (cliente) => {
    onClienteSeleccionado(cliente);
    setSearchTerm('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-purple-600" />
            Seleccionar Cliente
          </DialogTitle>
          <DialogDescription>
            Selecciona el cliente para el cual deseas crear un alistamiento
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Buscador */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar cliente por nombre o NIT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Lista de Clientes */}
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : filteredClientes.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <User className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  {searchTerm ? 'No se encontraron clientes' : 'No hay clientes en este centro de lavado'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredClientes.map((cliente) => (
                <Card 
                  key={cliente._id} 
                  className="hover:shadow-md transition-shadow cursor-pointer hover:border-purple-300"
                  onClick={() => handleSelectCliente(cliente)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-1">{cliente.nombre}</h3>
                        <p className="text-xs text-gray-500">NIT: {cliente.nit || 'N/A'}</p>
                        <p className="text-xs text-gray-400 mt-1">{cliente.direccion || 'Sin dirección'}</p>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {cliente.estado || 'Activo'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SeleccionarClienteModal;
